/**
 * Mode démo — point d'entrée web.
 *
 * engine.js / dictionaries.js / demoState.js sont PURS et copiés tels quels
 * dans la codebase mobile. Ce fichier (et firestoreProxy.js) est le câblage
 * spécifique web : locale i18n + purge du cache de données de référence au
 * toggle (il cache au-dessus du proxy Firestore, donc des noms réels).
 */

import i18n from "../i18n";
import { STAGE } from "../stage";
import { clearAll as clearRefDataCache } from "../utils/cache/refDataCache";
import {
  setLocaleProvider,
  setDemoPhotoResolver,
  hashString,
  substitute,
  exportNameSlots,
  importNameSlots,
  setNameSlotsListener,
} from "./engine";
import {
  onDemoModeChange,
  isDemoActive,
  isDemoPhotosActive as isDemoPhotosActiveState,
} from "./demoState";
import DEMO_PHOTO_PLACEHOLDER from "../assets/placeholders/photo.jpg";
import demoPhotoManifest from "./demoPhotoManifest.json";

setLocaleProvider(() => i18n.language);
onDemoModeChange(() => clearRefDataCache());

// Noms uniques : deux secteurs (enclos, groupes…) n'ont jamais le même nom
// fictif. Les attributions (empreintes des noms réels, jamais les noms) sont
// gardées dans le navigateur pour rester stables d'un rechargement à l'autre.
const NAME_SLOTS_STORAGE_KEY = "akongo.demoNameSlots";
try {
  importNameSlots(JSON.parse(window.localStorage.getItem(NAME_SLOTS_STORAGE_KEY)));
} catch (error) {
  // Stockage indisponible : les attributions se refont au fil des lectures
}
let nameSlotsSaveTimer = null;
setNameSlotsListener(() => {
  if (nameSlotsSaveTimer) return;
  nameSlotsSaveTimer = setTimeout(() => {
    nameSlotsSaveTimer = null;
    try {
      window.localStorage.setItem(NAME_SLOTS_STORAGE_KEY, JSON.stringify(exportNameSlots()));
    } catch (error) {
      // Stockage plein ou bloqué : sans conséquence
    }
  }, 500);
});

// --- Changement de langue en mode démo ---------------------------------------
// Les noms fictifs localisés (enclos, secteurs, groupes, zoos, tournées…) sont
// choisis au moment où le snapshot est livré, puis figés dans le state des
// vues et dans refDataCache. Un changement de langue ne re-livre rien : on
// purge le cache et on prévient les abonnés (Base.js remonte les vues).
const SUPPORTED_DEMO_LANGS = ["fr", "en", "de"];
const baseLang = (lng) => {
  const lang = String(lng || "en").toLowerCase().split("-")[0];
  return SUPPORTED_DEMO_LANGS.includes(lang) ? lang : "en";
};
const localeListeners = new Set();
let lastDemoLang = baseLang(i18n.language);

i18n.on("languageChanged", (lng) => {
  const next = baseLang(lng);
  if (next === lastDemoLang) return; // ré-émission (welfare variant) ou variante régionale
  lastDemoLang = next;
  if (!isDemoActive()) return;
  clearRefDataCache();
  localeListeners.forEach((listener) => {
    try {
      listener(next);
    } catch (error) {
      console.error("demo locale listener error", error);
    }
  });
});

/**
 * Abonne un callback aux changements de langue EFFECTIFS survenus en mode démo
 * (langue de base différente, démo actif). Retourne la fonction de désabonnement.
 */
export function onDemoLocaleChange(listener) {
  localeListeners.add(listener);
  return () => localeListeners.delete(listener);
}

// --- Photos factices : banque de 500 images IA (script/demoAssets) ----------
// Hébergée en lecture publique sous demo-assets/ dans le bucket Storage de
// l'environnement. Picking DÉTERMINISTE : hash de l'URL réelle → même photo
// factice partout (vignette comprise, seed unifié par objet côté engine).

const DEMO_ASSETS_BUCKET_BY_STAGE = {
  prod: "aws-prod-3fac0.appspot.com",
  QA: "aws-qa-3fcb7.appspot.com",
};
// dev/stage : la banque QA (publique) sert de fallback
const DEMO_ASSETS_BUCKET =
  DEMO_ASSETS_BUCKET_BY_STAGE[STAGE] || DEMO_ASSETS_BUCKET_BY_STAGE.QA;

const PHOTOS_BY_CATEGORY = demoPhotoManifest.images.reduce((acc, img) => {
  (acc[img.category] = acc[img.category] || []).push(img);
  return acc;
}, {});

// Champ photo → catégorie de la banque. Les champs non mappés (avatars de
// personnes, logos — traités par ailleurs) restent sur le placeholder neutre.
const PHOTO_FIELD_CATEGORY = {
  animalProfilePicture: "animals",
  animalPictureProfil: "animals",
  groupPhoto: "animals",
  groupProfilePicture: "animals",
  enclosureProfilePicture: "enclosures",
  sectorProfilePicture: "enclosures",
  profilePictureUrl: "enclosures",
  photo: "notes",
  photos: "notes",
  thumbnail: "notes",
  media: "notes",
  mediaList: "notes",
  mediaLocation: "notes",
  sessionPhoto: "notes",
  evaluationPhoto: "notes",
  imageUrl: "notes",
};

function demoPhotoUrl(category, seed, contextList) {
  const list =
    (contextList && contextList.length ? contextList : null) ||
    (category ? PHOTOS_BY_CATEGORY[category] : null);
  if (!list || list.length === 0) return DEMO_PHOTO_PLACEHOLDER;
  const img = list[hashString(seed) % list.length];
  return `https://storage.googleapis.com/${DEMO_ASSETS_BUCKET}/demo-assets/${img.file}`;
}

// --- Cohérence espèce ↔ photo -------------------------------------------------
// Les images animales du manifest portent l'espèce (labelFr/labelEn). Quand le
// document fournit specieName/specieLatinName, on restreint le picking aux
// photos de cette espèce (match par mots entiers, accents/pluriels tolérés).

const normWords = (s) =>
  String(s)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length > 1)
    .map((w) => w.replace(/s$/, ""));

// Égalité de mots avec tolérance de préfixe (≥5 chars) pour rapprocher les
// noms latins des noms communs ("giraffa" ~ "girafe", "panthera" ~ "panthère")
const wordsMatch = (a, b) =>
  a === b || (a.length >= 5 && b.length >= 5 && a.slice(0, 5) === b.slice(0, 5));

function matchAnimalPhotosBySpecies(speciesHints) {
  const hintWordSets = (speciesHints || []).map(normWords).filter((ws) => ws.length);
  if (!hintWordSets.length) return null;
  // Score = proportion des mots du label couverts par l'indice ; on ne garde
  // que les images au meilleur score, sinon "Lion" matcherait aussi le puma
  // ("mountain lion"), le tamarin lion doré et l'otarie ("sea lion").
  let best = 0;
  const scored = (PHOTOS_BY_CATEGORY.animals || []).map((img) => {
    const labelWordSets = [img.labelFr, img.labelEn].filter(Boolean).map(normWords);
    let score = 0;
    for (const lw of labelWordSets) {
      for (const hw of hintWordSets) {
        const shared = lw.filter((w) => hw.some((h) => wordsMatch(w, h))).length;
        if (shared) score = Math.max(score, shared / lw.length);
      }
    }
    if (score > best) best = score;
    return { img, score };
  });
  if (best === 0) return null;
  return scored.filter((s) => s.score === best).map((s) => s.img);
}

setDemoPhotoResolver((seed, fieldKey, nodeCtx) => {
  const category = PHOTO_FIELD_CATEGORY[fieldKey];
  const speciesMatches =
    category === "animals" && nodeCtx
      ? matchAnimalPhotosBySpecies(nodeCtx.species)
      : null;
  return demoPhotoUrl(category, seed, speciesMatches);
});

/**
 * Substitution pour les chemins qui résolvent une image via sa référence
 * Storage (imageController.getUrlFromStorageRef) et contournent donc les
 * champs d'URL anonymisés. Catégorie déduite du chemin du bucket
 * ({zooId}/animals/…, {zooId}/enclosures|sectors/…, animals/{id}/media/…).
 * Retourne null hors mode démo photos → l'appelant suit son chemin normal.
 */
export function demoPhotoForStorageRef(storageRef) {
  if (!isDemoPhotosActiveState()) return null;
  const ref = String(storageRef || "");
  if (!ref) return null;
  let category = "notes";
  if (/\/media\//.test(ref)) category = "notes";
  else if (/(^|\/)animals\//.test(ref)) category = "animals";
  else if (/(^|\/)(enclosures|sectors)\//.test(ref)) category = "enclosures";
  return demoPhotoUrl(category, ref);
}

/**
 * Fabrique des médias de galerie factices — pour remplir les galeries VIDES
 * en mode démo (une fiche sans galerie fait vide en présentation).
 * Retourne [{url, type, userName, timestamp}] : timestamps en secondes Unix
 * étalés sur les 3 dernières semaines, ~2/3 photos du sujet (espèce matchée
 * si fournie) + ~1/3 photos de terrain, le tout déterministe par seed.
 * Retourne [] hors mode démo photos — l'appelant garde sa liste vide normale.
 */
export function buildDemoGalleryMedia({ seed = "gallery", species = [], kind = "animal", count } = {}) {
  if (!isDemoPhotosActiveState()) return [];
  const n = count || 6 + (hashString(seed) % 5); // 6 à 10 items
  const subjectCategory = kind === "enclosure" ? "enclosures" : "animals";
  const subjectMatches =
    subjectCategory === "animals" ? matchAnimalPhotosBySpecies(species) : null;
  const now = Date.now();
  const items = [];
  for (let i = 0; i < n; i++) {
    const itemSeed = `${seed}#${i}`;
    const useFieldNote = i % 3 === 2;
    const url = useFieldNote
      ? demoPhotoUrl("notes", itemSeed)
      : demoPhotoUrl(subjectCategory, itemSeed, subjectMatches);
    const date = new Date(now - (hashString(itemSeed) % 21) * 86400000);
    date.setHours(8 + (hashString(itemSeed + "h") % 10), hashString(itemSeed + "m") % 60, 0, 0);
    items.push({
      url,
      type: "image",
      userName: substitute("fullName", `${seed}-soigneur-${i % 3}`),
      timestamp: Math.floor(date.getTime() / 1000),
    });
  }
  return items.sort((a, b) => b.timestamp - a.timestamp);
}

export {
  isDemoActive,
  isDemoPhotosActive,
  setDemoActive,
  setDemoPhotosActive,
  onDemoModeChange,
} from "./demoState";
export {
  anonymizeDoc,
  anonymizeFullName,
  substitute,
  demoSearchMatch,
  hashString,
  getRafikiDemoInstruction,
  setLocaleProvider,
  setDemoPhotoResolver,
} from "./engine";
export {
  wrapFirestore,
  wrapRealTimeDb,
  wrapFunctions,
  wrapStorage,
  wrapSnapshot,
  unwrapDemoSnapshot,
  isCallableBlockedInDemo,
} from "./firestoreProxy";
export { DEMO_WRITE_BLOCKED, isDemoWriteBlockedError } from "./demoWriteBlock";
export { demoZooLogoPlaceholder } from "./demoPlaceholders";
